import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Box, Center, Text as MantineText } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { TFunction } from "i18next";

import { Button, Loader, Modal, Textarea } from "components";
import { MotivationalMessageRequest, MotivationalMessageResponse } from "types";
import { MotivationalMessageService } from "services";

import classes from "./MotivationalMessagesPage.module.css";

interface MotivationalMessageFormModalProps {
  messageId?: number;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
  opened: boolean;
  t: TFunction;
}

type FormValues = Record<"DE" | "EN" | "SR", string>;

const EMPTY_FORM_VALUES: FormValues = {
  DE: "",
  EN: "",
  SR: "",
};

const SUPPORTED_LANGUAGES: Array<keyof FormValues> = ["DE", "EN", "SR"];

const mapResponseToFormValues = (message: MotivationalMessageResponse): FormValues => {
  const nextValues = { ...EMPTY_FORM_VALUES };

  message.translations.forEach((translation) => {
    if (translation.language in nextValues) {
      nextValues[translation.language as keyof FormValues] = translation.text;
    }
  });

  return nextValues;
};

const buildPayload = (values: FormValues): MotivationalMessageRequest => ({
  translations: SUPPORTED_LANGUAGES.map((language) => ({
    language,
    text: values[language].trim(),
  })),
});

const MotivationalMessageFormModal = ({
  messageId,
  onClose,
  onSaved,
  opened,
  t,
}: MotivationalMessageFormModalProps) => {
  const isEditMode = Boolean(messageId);
  const [isLoadingMessage, setIsLoadingMessage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormValues>({
    initialValues: EMPTY_FORM_VALUES,
    validate: {
      DE: (value) => value.trim().length === 0 ? t("error.required") : null,
      EN: (value) => value.trim().length === 0 ? t("error.required") : null,
      SR: (value) => value.trim().length === 0 ? t("error.required") : null,
    },
  });
  const formRef = useRef(form);
  const tRef = useRef(t);

  useEffect(() => {
    formRef.current = form;
  }, [form]);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const modalTitle = useMemo(
    () =>
      isEditMode
        ? t("motivationalMessages.form.editTitle")
        : t("motivationalMessages.form.createTitle"),
    [isEditMode, t],
  );

  useEffect(() => {
    if (!opened) {
      formRef.current.reset();
      formRef.current.clearErrors();
      setIsLoadingMessage(false);
      setIsSubmitting(false);
    }
  }, [opened]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (!messageId) {
      formRef.current.reset();
      formRef.current.clearErrors();
      return;
    }

    let isActive = true;

    formRef.current.setValues(EMPTY_FORM_VALUES);
    setIsLoadingMessage(true);

    void MotivationalMessageService.getMotivationalMessage(messageId)
      .then((data) => {
        if (!isActive) {
          return;
        }

        formRef.current.setValues(mapResponseToFormValues(data));
        formRef.current.clearErrors();
      })
      .catch(() => {
        if (!isActive) {
          return;
        }

        notifications.show({
          color: "red",
          message: tRef.current("motivationalMessages.notifications.loadErrorMessage"),
          title: tRef.current("motivationalMessages.notifications.loadErrorTitle"),
        });
        onClose();
      })
      .finally(() => {
        if (isActive) {
          setIsLoadingMessage(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [messageId, onClose, opened]);

  const handleSubmit = useCallback(async () => {
    const validation = form.validate();

    if (validation.hasErrors) {
      return;
    }

    const payload = buildPayload(form.getValues());

    setIsSubmitting(true);

    try {
      if (messageId) {
        await MotivationalMessageService.updateMotivationalMessage(messageId, payload);
      } else {
        await MotivationalMessageService.createMotivationalMessage(payload);
      }

      notifications.show({
        color: "green",
        message: t(
          messageId
            ? "motivationalMessages.notifications.updateSuccessMessage"
            : "motivationalMessages.notifications.createSuccessMessage",
        ),
        title: t("motivationalMessages.notifications.saveSuccessTitle"),
      });

      await onSaved();
      onClose();
    } catch {
      notifications.show({
        color: "red",
        message: t(
          messageId
            ? "motivationalMessages.notifications.updateErrorMessage"
            : "motivationalMessages.notifications.createErrorMessage",
        ),
        title: t("motivationalMessages.notifications.saveErrorTitle"),
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [form, messageId, onClose, onSaved, t]);

  return (
    <Modal opened={opened} onClose={onClose} title={modalTitle}>
      <Box pos="relative">
        <Loader overlay={true} visible={isLoadingMessage} />

        <MantineText className={classes.modalHint}>
          {t("motivationalMessages.form.hint")}
        </MantineText>

        {SUPPORTED_LANGUAGES.map((language) => (
          <Textarea
            key={language}
            autosize={true}
            label={language}
            minRows={4}
            mb="md"
            required={true}
            {...form.getInputProps(language)}
          />
        ))}

        <Center className={classes.modalActions}>
          <Button
            disabled={isLoadingMessage || isSubmitting}
            loading={isSubmitting}
            onClick={handleSubmit}
          >
            {isEditMode ? t("action.save") : t("action.create")}
          </Button>
        </Center>
      </Box>
    </Modal>
  );
};

export default MotivationalMessageFormModal;
